/**
 * PriceBundle - Custom Element for Multi-Step Product Bundle Selection
 * Handles up to 3 steps with configurable product selection and pricing
 * Enhanced with total price calculation and display
 */
class PriceBundle extends HTMLElement {
    constructor() {
        super();
        
        // State management
        this.state = {
            currentStep: 1,
            selections: {
                step1: new Map(), // Map<variantId, quantity>
                step2: new Map(),
                step3: new Map()
            },
            prices: {
                step1: new Map(), // Map<variantId, price>
                step2: new Map(),
                step3: new Map()
            },
            config: null,
            productId: null,
            variantId: null
        };
        
        // Bind methods
        this.init = this.init.bind(this);
        this.setupEventListeners = this.setupEventListeners.bind(this);
        this.handleQuantityChange = this.handleQuantityChange.bind(this);
        this.handleNextStep = this.handleNextStep.bind(this);
        this.handleItemSelection = this.handleItemSelection.bind(this);
        this.cart = document.querySelector("cart-notification") || document.querySelector("cart-drawer");

    }
    
    connectedCallback() {
        this.init();
    }
    
    /**
     * Initialize the component
     */
    init() {
        // Get product and variant IDs
        this.state.productId = this.getAttribute('data-pid');
        this.state.variantId = this.getAttribute('data-vid');
        
        // Parse configuration
        this.parseConfig();
        
        // Store prices from DOM
        this.storePricesFromDOM();
        
        // Setup event listeners
        this.setupEventListeners();
        
        // Initialize first step
        this.activateStep(1);
    }
    
    /**
     * Store prices from bundle items in DOM
     */
    storePricesFromDOM() {
        // Get all bundle items and extract prices
        this.querySelectorAll('.bundle-item').forEach(item => {
            const variantId = item.getAttribute('data-vid');
            const priceElement = item.querySelector('[data-price]');
            
            if (variantId && priceElement) {
                const price = parseFloat(priceElement.getAttribute('data-price')) || 0;
                const step = this.getCurrentStepNumber(item);
                const stepKey = `step${step}`;
                
                this.state.prices[stepKey].set(variantId, price);
            }
        });
    }
    
    /**
     * Parse configuration from JSON object in DOM
     */
    parseConfig() {
        const configElement = this.querySelector('.custom-bundle-price-json-objects');
        if (configElement) {
            try {
                const configText = configElement.textContent.trim();
                this.state.config = JSON.parse(configText);
                
                // Create product lookup maps for easy access
                this.state.productLookup = {
                    step1: new Map(),
                    step2: new Map(),
                    step3: new Map()
                };
                
                // Populate step 1 products
                if (this.state.config.first_step_products) {
                    this.state.config.first_step_products.forEach(product => {
                        if (product.variants) {
                            product.variants.forEach(variant => {
                                this.state.productLookup.step1.set(variant.id.toString(), {
                                    productTitle: product.title,
                                    variantTitle: variant.title,
                                    variantId: variant.id
                                });
                            });
                        }
                    });
                }
                
                // Populate step 2 products
                if (this.state.config.second_step_products) {
                    this.state.config.second_step_products.forEach(product => {
                        if (product.variants) {
                            product.variants.forEach(variant => {
                                this.state.productLookup.step2.set(variant.id.toString(), {
                                    productTitle: product.title,
                                    variantTitle: variant.title,
                                    variantId: variant.id
                                });
                            });
                        }
                    });
                }
                
                // Populate step 3 products
                if (this.state.config.third_step_products) {
                    this.state.config.third_step_products.forEach(product => {
                        if (product.variants) {
                            product.variants.forEach(variant => {
                                this.state.productLookup.step3.set(variant.id.toString(), {
                                    productTitle: product.title,
                                    variantTitle: variant.title,
                                    variantId: variant.id
                                });
                            });
                        }
                    });
                }
                
            } catch (error) {
                console.error('Failed to parse bundle configuration:', error);
                this.state.config = {
                    enable_second_step: false,
                    enable_third_step: false,
                    main_max_products: 1,
                    second_step_max_products: 1,
                    third_step_max_products: 1,
                    second_step_free: false,
                    third_step_free: false,
                    first_step_products: [],
                    second_step_products: [],
                    third_step_products: []
                };
                this.state.productLookup = {
                    step1: new Map(),
                    step2: new Map(),
                    step3: new Map()
                };
            }
        }
    }
    
    /**
     * Get formatted product title for a variant
     * If variant title is "Default Title", return only product name
     * Otherwise, return "Product Name - Variant Title"
     */
    getProductTitle(variantId, step) {
        const stepKey = `step${step}`;
        const productInfo = this.state.productLookup[stepKey].get(variantId.toString());
        
        if (!productInfo) {
            return '';
        }
        
        // Check if variant title is "Default Title" (case-insensitive)
        if (productInfo.variantTitle.toLowerCase() === 'default title') {
            return productInfo.productTitle;
        }
        
        return `${productInfo.productTitle} - ${productInfo.variantTitle}`;
    }
    
    /**
     * Setup all event listeners
     */
    setupEventListeners() {
        // Quantity opener buttons (for steps 1 and 3)
        this.querySelectorAll('.quanity-opner').forEach(button => {
            button.addEventListener('click', (e) => {
                const bundleItem = e.target.closest('.bundle-item');
                this.toggleQuantityBox(bundleItem);
            });
        });
        
        // Plus/Minus quantity buttons
        this.querySelectorAll('.quantity-button').forEach(button => {
            button.addEventListener('click', (e) => {
                const action = button.classList.contains('plus') ? 'increment' : 'decrement';
                const bundleItem = e.target.closest('.bundle-item');
                this.handleQuantityChange(bundleItem, action);
            });
        });
        
        // Step 2 item selection (click to select for vertical layout)
        const step2Wrapper = this.querySelector('[data-step="2"]');
        if (step2Wrapper) {
            step2Wrapper.querySelectorAll('.bundle-item').forEach(item => {
                item.addEventListener('click', (e) => {
                    if (!e.target.closest('.quantity-button')) {
                        this.handleItemSelection(item, 2);
                    }
                });
            });
        }
        
        // Next/Submit buttons
        this.querySelectorAll('.bundle-button').forEach(button => {
            button.addEventListener('click', (e) => {
                this.handleNextStep(e.currentTarget);
            });
        });
        this.querySelectorAll('.step-back').forEach(button => {
            button.addEventListener('click', (e) => {
                this.activateStep(parseInt(button.dataset.step));
            });
        });
    }
    
    /**
     * Toggle quantity box visibility
     */
    toggleQuantityBox(bundleItem) {
        const opener = bundleItem.querySelector('.quanity-opner');
        const quantityWrapper = bundleItem.querySelector('.bundle-item-quantity-wrapper');
        
        if (quantityWrapper && opener) {
            opener.classList.add('hidden');
            quantityWrapper.classList.remove('hidden');
            
            // Automatically set quantity to 1
            //this.setItemQuantity(bundleItem, 1);
            bundleItem.querySelector(".quantity-button.plus").click();
        }
    }
    
    /**
     * Handle quantity increment/decrement
     */
    handleQuantityChange(bundleItem, action) {
        const input = bundleItem.querySelector('.custom-qty-box-input');
        const displayQty = bundleItem.querySelector('.display-qty');
        const minusBtn = bundleItem.querySelector('.quantity-button.minus');
        const opener = bundleItem.querySelector('.quanity-opner');
        const quantityWrapper = bundleItem.querySelector('.bundle-item-quantity-wrapper');
        
        let currentQty = parseInt(input.value) || 0;
        const step = this.getCurrentStepNumber(bundleItem);
        
        // Calculate new quantity
        if (action === 'increment') {
            const maxAllowed = this.getMaxQuantityForStep(step);
            const currentTotal = this.getTotalQuantityForStep(step);
            
            if (currentTotal < maxAllowed) {
                currentQty++;
            }
        } else if (action === 'decrement') {
            currentQty = Math.max(0, currentQty - 1);
        }
        
        // Update quantity
        this.setItemQuantity(bundleItem, currentQty);
        
        // Update UI
        input.value = currentQty;
        displayQty.textContent = currentQty;
        
        // Handle minus button state
        if (currentQty === 0) {
            minusBtn.setAttribute('disabled', 'disabled');
            if (opener && quantityWrapper) {
                opener.classList.remove('hidden');
                quantityWrapper.classList.add('hidden');
            }
        } else {
            minusBtn.removeAttribute('disabled');
        }
        
        // Update step counter and button state
        this.updateStepCounter(step);
        this.updateButtonState(step);
        
        // Update non-selected items opacity and disabled state
        this.updateNonSelectedItems(step);
    }
    
    /**
     * Handle item selection for step 2 (single/multi-select)
     */
    handleItemSelection(item, step) {
        const variantId = item.getAttribute('data-vid');
        const maxProducts = parseInt(this.state.config.second_step_max_products) || 1;
        const currentSelections = this.state.selections.step2;
        
        // Toggle selection
        if (currentSelections.has(variantId)) {
            currentSelections.delete(variantId);
            item.classList.remove('selected');
        } else {
            // Check if we've reached max selections
            if (currentSelections.size >= maxProducts) {
                // Remove oldest selection if single-select
                if (maxProducts === 1) {
                    const firstKey = currentSelections.keys().next().value;
                    currentSelections.delete(firstKey);
                    this.querySelector(`[data-step="2"] [data-vid="${firstKey}"]`)?.classList.remove('selected');
                } else {
                    return; // Don't allow more selections
                }
            }
            
            currentSelections.set(variantId, 1);
            item.classList.add('selected');
        }
        
        // Update counter and button
        this.updateStepCounter(step);
        this.updateButtonState(step);
        
        // Update non-selected items opacity and disabled state
        if(step != 2){
            this.updateNonSelectedItems(step);
        }
        
    }
    
    /**
     * Set quantity for a specific item
     */
    setItemQuantity(bundleItem, quantity) {
        const step = this.getCurrentStepNumber(bundleItem);
        const variantId = bundleItem.getAttribute('data-vid');
        const stepKey = `step${step}`;
        
        if (quantity > 0) {
            this.state.selections[stepKey].set(variantId, quantity);
        } else {
            this.state.selections[stepKey].delete(variantId);
        }
    }
    
    /**
     * Get current step number for a bundle item
     */
    getCurrentStepNumber(bundleItem) {
        const stepWrapper = bundleItem.closest('[data-step]');
        return parseInt(stepWrapper?.getAttribute('data-step')) || 1;
    }
    
    /**
     * Get total quantity selected for a step
     */
    getTotalQuantityForStep(step) {
        const stepKey = `step${step}`;
        let total = 0;
        
        this.state.selections[stepKey].forEach(qty => {
            total += qty;
        });
        
        return total;
    }
    
    /**
     * Get total price for a specific step (excluding free items)
     */
    getTotalPriceForStep(step) {
        const stepKey = `step${step}`;
        let total = 0;
        
        // Check if this step is free
        const isFree = (step === 2 && (this.state.config.second_step_free === 'true' || this.state.config.second_step_free === true)) ||
                       (step === 3 && (this.state.config.third_step_free === 'true' || this.state.config.third_step_free === true));
        
        if (isFree) {
            return 0;
        }
        
        this.state.selections[stepKey].forEach((quantity, variantId) => {
            const price = this.state.prices[stepKey].get(variantId) || 0;
            total += price * quantity;
        });
        
        return total;
    }
    
    /**
     * Get cumulative total price up to and including the current step
     */
    getCumulativeTotalPrice(upToStep) {
        let total = 0;
        
        for (let step = 1; step <= upToStep; step++) {
            total += this.getTotalPriceForStep(step);
        }
        
        return total;
    }
    
    /**
     * Format price according to Shopify money format
     */
    formatPrice(cents) {
        return theme.Shopify.formatMoney(cents, theme.money_format)
       
    }
    
    /**
     * Get max quantity allowed for a step
     */
    getMaxQuantityForStep(step) {
        switch(step) {
            case 1:
                return parseInt(this.state.config.main_max_products) || 1;
            case 2:
                return parseInt(this.state.config.second_step_max_products) || 1;
            case 3:
                return parseInt(this.state.config.third_step_max_products) || 1;
            default:
                return 1;
        }
    }
    
    /**
     * Update step counter display with quantity and price
     */
    updateStepCounter(step) {
        const stepWrapper = this.querySelector(`[data-step="${step}"]`);
        if (!stepWrapper) return;
        
        const counter = stepWrapper.querySelector('.counter');
        if (counter) {
            const total = this.getTotalQuantityForStep(step);
            const max = this.getMaxQuantityForStep(step);
            const price = this.getTotalPriceForStep(step);
            
            // Display quantity
            counter.textContent = `${total}/${max}`;
            
            // Display price if there's a price element
            const priceDisplay = stepWrapper.querySelector('.counter-price');
            if (priceDisplay) {
                priceDisplay.textContent = this.formatPrice(price);
            }
        }
    }
    
    /**
     * Update button state (enabled/disabled), text visibility, and price display
     */
    updateButtonState(step) {
        const stepWrapper = this.querySelector(`[data-step="${step}"]`);
        if (!stepWrapper) return;
        
        const button = stepWrapper.querySelector('.bundle-button');
        const beforeText = button.querySelector('.before-selection-text');
        const afterText = button.querySelector('.after-selection-text');
        const buttonSvg = button.querySelector('.button-svg-wrapper');
        const priceDisplay = button.querySelector('.button-price');
        
        const total = this.getTotalQuantityForStep(step);
        const max = this.getMaxQuantityForStep(step);
        
        // Determine if this is the last step
        const isLastStep = !button.getAttribute('data-next');
        
        // Calculate price to display
        let displayPrice;
        if (isLastStep) {
            // For last step, show cumulative total of all steps
            displayPrice = this.getCumulativeTotalPrice(step);
        } else {
            // For intermediate steps, show only current step price
            displayPrice = this.getTotalPriceForStep(step);
        }
        
        if (total === max && total > 0) {
            // Enable button when selection is complete
            button.removeAttribute('disabled');
            button.classList.add('active');
            
            // Show after-selection text and hide before-selection text
            if (beforeText) beforeText.classList.add('hidden');
            if (afterText) afterText.classList.remove('hidden');
            if (buttonSvg) buttonSvg.classList.remove('hidden');
            
            // Update price display
            if (priceDisplay) {
                priceDisplay.textContent = this.formatPrice(displayPrice);
                priceDisplay.classList.remove('hidden');
            }
        } else {
            // Disable button when selection is incomplete
            button.setAttribute('disabled', 'disabled');
            button.classList.remove('active');
            
            // Show before-selection text and hide after-selection text
            if (beforeText) beforeText.classList.remove('hidden');
            if (afterText) afterText.classList.add('hidden');
            if (buttonSvg) buttonSvg.classList.add('hidden');
            
            // Hide price display when button is disabled
            if (priceDisplay) {
                priceDisplay.classList.add('hidden');
            }
        }
    }
    
    /**
     * Update non-selected items (add opacity and disable) when max is reached
     */
    updateNonSelectedItems(step) {
        const stepWrapper = this.querySelector(`[data-step="${step}"]`);
        if (!stepWrapper) return;
        
        const total = this.getTotalQuantityForStep(step);
        const max = this.getMaxQuantityForStep(step);
        const isMaxReached = total >= max;
        
        const stepKey = `step${step}`;
        const selections = this.state.selections[stepKey];
        
        // Get all bundle items in this step
        const bundleItems = stepWrapper.querySelectorAll('.bundle-item');
        
        bundleItems.forEach(item => {
            const variantId = item.getAttribute('data-vid');
            const isSelected = selections.has(variantId) && selections.get(variantId) > 0;
            const opener = item.querySelector('.quanity-opner');
            
            if (isMaxReached && !isSelected) {
                // Add opacity class to non-selected items
                item.classList.add('opacity-5');
                
                // Disable the quantity opener button
                if (opener) {
                    opener.setAttribute('disabled', 'disabled');
                }
                
                // For step 2, also prevent clicking
                if (step === 2) {
                    item.style.pointerEvents = 'none';
                }
            } else {
                // Remove opacity class and enable
                item.classList.remove('opacity-5');
                
                // Enable the quantity opener button
                if (opener) {
                    opener.removeAttribute('disabled');
                }
                
                // For step 2, restore clicking
                if (step === 2) {
                    item.style.pointerEvents = '';
                }
            }
        });
    }
    
    /**
     * Handle next step or final submission
     */
    handleNextStep(button) {
        const currentStepWrapper = button.closest('[data-step]');
        const currentStep = parseInt(currentStepWrapper.getAttribute('data-step'));
        const nextStep = button.getAttribute('data-next');
        
        if (nextStep) {
            // Move to next step
            this.activateStep(parseInt(nextStep));
        } else {
            // Final step - submit bundle to cart
            this.submitBundle();
        }
    }
    
    /**
     * Activate a specific step
     */
    activateStep(step) {
        // Hide all steps
        this.querySelectorAll('.price-bundle-step-wrapper').forEach(wrapper => {
            wrapper.classList.remove('active');
            wrapper.classList.add('hidden');
        });
        
        // Show target step
        const targetStep = this.querySelector(`[data-step="${step}"]`);
        if (targetStep) {
            targetStep.classList.add('active');
            targetStep.classList.remove('hidden');
            this.state.currentStep = step;
            
            // Update button state for the new step
            this.updateButtonState(step);
            
            // Scroll to top of step
            const scroller = targetStep.querySelector('.step-item-scroller');
            if (scroller) {
                scroller.scrollTop = 0;
            }
        }
    }
    
    /**
     * Submit bundle to cart
     */
    async submitBundle() {
        const items = this.collectBundleItems();
        
        if (items.length === 0) {
            console.error('No items selected');
            return;
        }
        
        // Dispatch custom event with bundle data
        const bundleEvent = new CustomEvent('bundle:submit', {
            detail: {
                items: items,
                productId: this.state.productId,
                variantId: this.state.variantId,
                totalPrice: this.getCumulativeTotalPrice(this.state.currentStep)
            },
            bubbles: true
        });
        
        this.dispatchEvent(bundleEvent);
        
        // Default cart submission (can be overridden by event listener)
        try {
            const response = await this.addToCart(items);
            
            if (response.ok) {
                 if (!this.cart) {
                    window.location = window.routes.cart_url;
                    return;
                }
                const data = await response.json();

                publish(PUB_SUB_EVENTS.cartUpdate, {source: 'product-form', productVariantId: this.state.variantId});
                this.cart.renderContents(data);

                // Dispatch success event
                this.dispatchEvent(new CustomEvent('bundle:success', {
                    detail: { data },
                    bubbles: true
                }));
                if(this.closest(".custom-modal__centered.active")){
                     this.closest(".custom-modal__centered.active").classList.remove("active");
                document.body.classList.remove("custom-model-open");
                }
               
                if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            } else {
                throw new Error('Failed to add to cart');
            }
        } catch (error) {
            console.error('Error adding bundle to cart:', error);
            this.dispatchEvent(new CustomEvent('bundle:error', {
                detail: { error },
                bubbles: true
            }));
        }
    }
    
    /**
     * Collect all selected bundle items
     */
    collectBundleItems() {
        const items = [];
        
        // Step 1 items
        this.state.selections.step1.forEach((quantity, variantId) => {
            items.push({
                id: variantId,
                quantity: quantity,
                step: 1,
                price: this.state.prices.step1.get(variantId) || 0
            });
        });
        
        // Step 2 items (may be free)
        this.state.selections.step2.forEach((quantity, variantId) => {
            const isFree = this.state.config.second_step_free === 'true' || this.state.config.second_step_free === true;
            items.push({
                id: variantId,
                quantity: quantity,
                step: 2,
                free: isFree,
                price: isFree ? 0 : (this.state.prices.step2.get(variantId) || 0)
            });
        });
        
        // Step 3 items (may be free)
        this.state.selections.step3.forEach((quantity, variantId) => {
            const isFree = this.state.config.third_step_free === 'true' || this.state.config.third_step_free === true;
            items.push({
                id: variantId,
                quantity: quantity,
                step: 3,
                free: isFree,
                price: isFree ? 0 : (this.state.prices.step3.get(variantId) || 0)
            });
        });
        
        return items;
    }
    
    /**
     * Generate unique bundle ID
     */
    generateBundleId() {
        return `bundle_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    }
    
    /**
     * Generate Contenu property value with all selected products
     */
    generateContenuProperty(items) {
        const productTitles = items.map(item => {
            const title = this.getProductTitle(item.id, item.step);
            return item.quantity > 1 ? `${title} x${item.quantity}` : title;
        });
        
        return productTitles.join(', ');
    }
    
    /**
     * Add items to Shopify cart
     */
    async addToCart(items) {
        const bundleId = this.generateBundleId();
        const contenuValue = this.generateContenuProperty(items);
        
        // First add the main product, then bundle items
        const allItems = [
            // Main product
            {
                id: this.state.variantId,
                quantity: 1,
                properties: {
                    _customBundleId: bundleId,
                    _isMainProduct: 1,
                    _isPriceInclude: 1,
                    Contenu: contenuValue
                }
            },
            // Bundle items
            ...items.map(item => ({
                id: item.id,
                quantity: item.quantity,
                properties: {
                    _bundle_step: item.step,
                    _bundle_parent: this.state.productId,
                    _customBundleId: bundleId,
                    _isMainProduct: 0,
                    _isPriceInclude: item.free ? 0 : 1,
                    _baseQty:1
                }
            }))
        ];
        
        const formData = {
            items: allItems,
            sections:this.cart.getSectionsToRender().map((section) => section.id),
            sections_url:window.location.pathname
        };
        
        return fetch('/cart/add.js', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(formData)
        });
    }
    
    /**
     * Reset bundle selections
     */
    reset() {
        this.state.selections.step1.clear();
        this.state.selections.step2.clear();
        this.state.selections.step3.clear();
        
        // Reset UI
        this.querySelectorAll('.custom-qty-box-input').forEach(input => {
            input.value = 0;
        });
        
        this.querySelectorAll('.display-qty').forEach(display => {
            display.textContent = '0';
        });
        
        this.querySelectorAll('.bundle-item').forEach(item => {
            item.classList.remove('selected');
            item.classList.remove('opacity-5');
            item.style.pointerEvents = '';
        });
        
        this.querySelectorAll('.bundle-item-quantity-wrapper').forEach(wrapper => {
            wrapper.classList.add('hidden');
        });
        
        this.querySelectorAll('.quanity-opner').forEach(opener => {
            opener.classList.remove('hidden');
            opener.removeAttribute('disabled');
        });
        
        // Reset counters
        this.querySelectorAll('[data-step]').forEach(stepWrapper => {
            const step = parseInt(stepWrapper.getAttribute('data-step'));
            this.updateStepCounter(step);
            this.updateButtonState(step);
        });
        
        // Reset to first step
        this.activateStep(1);
    }
    
    /**
     * Get current bundle state
     */
    getState() {
        return {
            currentStep: this.state.currentStep,
            selections: {
                step1: Array.from(this.state.selections.step1.entries()),
                step2: Array.from(this.state.selections.step2.entries()),
                step3: Array.from(this.state.selections.step3.entries())
            },
            items: this.collectBundleItems(),
            totalPrice: this.getCumulativeTotalPrice(this.state.currentStep)
        };
    }
}

// Register the custom element
if (!customElements.get('price-bundle')) {
    customElements.define('price-bundle', PriceBundle);
}

// Export for use in modules
if (typeof module !== 'undefined' && module.exports) {
    module.exports = PriceBundle;
}